import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import {
  EmailAuthProvider,
  getAuth,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  User,
} from 'firebase/auth';
import { getMe, updateMe as apiUpdateMe, forgotPassword as apiForgotPassword, Operator } from '../lib/api/operators.js';

// Get runtime configuration from window.RUNTIME_CONFIG (injected by Docker entrypoint)
declare global {
  interface Window {
    RUNTIME_CONFIG: {
      FIREBASE_API_KEY: string;
      FIREBASE_AUTH_DOMAIN: string;
      FIREBASE_PROJECT_ID: string;
      FIREBASE_STORAGE_BUCKET: string;
      FIREBASE_MESSAGING_SENDER_ID: string;
      FIREBASE_APP_ID: string;
    };
  }
}

const firebaseConfig = {
  apiKey: window.RUNTIME_CONFIG.FIREBASE_API_KEY,
  authDomain: window.RUNTIME_CONFIG.FIREBASE_AUTH_DOMAIN,
  projectId: window.RUNTIME_CONFIG.FIREBASE_PROJECT_ID,
  storageBucket: window.RUNTIME_CONFIG.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: window.RUNTIME_CONFIG.FIREBASE_MESSAGING_SENDER_ID,
  appId: window.RUNTIME_CONFIG.FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Re-exported so callers (e.g. the admin Invite modal that follows up with
// sendPasswordResetEmail) can use the same configured auth instance.
export { auth };

interface AuthContextType {
  user: User | null;
  operator: Operator | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshOperator: () => Promise<void>;
  hasRole: (role: string) => boolean;
  resetPassword: (email: string) => Promise<void>;
  reauthenticateAndUpdatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateDisplayName: (displayName: string | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [operator, setOperator] = useState<Operator | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOperator = useCallback(async () => {
    try {
      const me = await getMe();
      setOperator(me);
    } catch (err) {
      // If the backend rejects (e.g. account disabled), surface as no operator
      // — RequireRole will route to /no-access.
      console.error('Failed to load operator profile', err);
      setOperator(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        await fetchOperator();
      } else {
        setOperator(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [fetchOperator]);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    // operator load happens via the onAuthStateChanged listener
  };

  const logout = async () => {
    await signOut(auth);
    setOperator(null);
  };

  const refreshOperator = async () => {
    await fetchOperator();
  };

  const hasRole = (role: string): boolean => {
    if (!operator) return false;
    if (operator.roles.includes('super_admin')) return true;
    return operator.roles.includes(role);
  };

  const resetPassword = async (email: string) => {
    // Backend generates the reset link + sends via Mailjet. Firebase's own
    // email path is unusable on this project (template/Action-URL edits are
    // blocked — see OPERATOR_ROLES_PLAN.md). Always resolves (no enumeration).
    await apiForgotPassword(email);
  };

  const reauthenticateAndUpdatePassword = async (
    currentPassword: string,
    newPassword: string,
  ) => {
    if (!user || !user.email) {
      throw new Error('Not signed in');
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  };

  const updateDisplayName = async (displayName: string | null) => {
    const updated = await apiUpdateMe({ display_name: displayName });
    setOperator(updated);
  };

  const value: AuthContextType = {
    user,
    operator,
    loading,
    signIn,
    logout,
    refreshOperator,
    hasRole,
    resetPassword,
    reauthenticateAndUpdatePassword,
    updateDisplayName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
